import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./utils";

export const locationApis = createApi({
  reducerPath: "locationApis",
  baseQuery: baseQueryWithReauth,

  // Important: add tag types you'll use for cache invalidation
  tagTypes: ["Location", "ExtraPincode", "ExtraPincodeList"],

  endpoints: (builder) => ({
    // ── Existing ────────────────────────────────────────────────────────
    getStateList: builder.query({
      query: () => `getState`,
      providesTags: ["Location"],
    }),

    getCityList: builder.query({
      query: (stateId) => `getCity/${stateId}`,
      providesTags: ["Location"],
    }),

    createPincode: builder.mutation({
      query: (data) => {
        return {
          url: `CreatePincode`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["ExtraPincodeList", "ExtraPincode"],
    }),

    // ── Get all extra pincodes for a specific city ─────────────────
    getExtraPincodesByCity: builder.query({
      query: (cityId) => `GetExtraPincodeByCity/${cityId}`,
      providesTags: (result, error, cityId) =>
        result?.data
          ? [
            "ExtraPincodeList",
            ...result.data.map(({ id }) => ({ type: "ExtraPincode", id })),
            { type: "ExtraPincodeList", id: cityId },
          ]
          : ["ExtraPincodeList"],
    }),

    // ── Delete a single extra pincode by its ID ────────────────────
    deleteExtraPincode: builder.mutation({
      query: (pincodeId) => ({
        url: `DeleteExtraPincode/${pincodeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, pincodeId) => [
        { type: "ExtraPincode", id: pincodeId },
        "ExtraPincodeList",
      ],
    }),

    // ✅ NEW: Update an existing extra pincode ──────────────────────
    updateExtraPincode: builder.mutation({
      query: ({ id, pincode }) => ({
        url: `UpdateExtraPincode/${id}`,
        method: "PUT",
        body: { pincode },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ExtraPincode", id },
        "ExtraPincodeList",
      ],
    }),

    // ✅ NEW (Optional): Check if pincode has associated orders ─────
    checkPincodeOrderAssociation: builder.query({
      query: (pincodeId) => `CheckPincodeOrderAssociation/${pincodeId}`,
      // Don't cache this - always fetch fresh
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetStateListQuery,
  useGetCityListQuery,
  useCreatePincodeMutation,
  useGetExtraPincodesByCityQuery,
  useDeleteExtraPincodeMutation,
  useUpdateExtraPincodeMutation, // ✅ NEW export
  useCheckPincodeOrderAssociationQuery, // ✅ NEW export (optional)
} = locationApis;